<template>
    <aside class="editor-page-settings">
        <h4>{{ i18n("voucherDesign.pageSettings") }}</h4>
        <EditorBackgroundPanel
            v-model:backgroundImage="editorBackgroundImageModel"
            v-model:backgroundImageId="editorBackgroundImageIdModel"
            :location-id="props.locationId"
        />
        <EditorBackgroundColorPanel :editor="props.editor" v-model:backgroundColor="editorBackgroundColorModel" />
        <EditorVariablesPanel ref="variablesPanel" :editor="props.editor" :locationDetail="props.locationDetail" />
        <EditorPredefinedImagesPanel ref="imagesPanel" :editor="props.editor" :locationDetail="props.locationDetail" />
        <EditorImageLibraryPanel :editor="props.editor" :location-id="props.locationId" />
        <div>
            <label>{{ i18n("voucherDesign.emptyTextBox") }}:</label>
            <div>
                <button type="button" class="btn btn-sm btn-success" @click="addAbsoluteTextBox">
                    {{ i18n("voucherDesign.addEmptyTextBox") }}
                </button>
            </div>
        </div>
    </aside>
</template>
<script setup lang="ts">
    import type { ILocationDetail } from "@/data/model/Location";
    import { i18n } from "@/portal/Trans";
    import type { Editor } from "@tiptap/vue-3";
    import { computed, ref } from "vue";

    import EditorBackgroundColorPanel from "./pageSettings/EditorBackgroundColorPanel.vue";
    import EditorBackgroundPanel from "./pageSettings/EditorBackgroundPanel.vue";
    import EditorImageLibraryPanel from "./pageSettings/EditorImageLibraryPanel.vue";
    // import EditorMarginSizePanel from "./pageSettings/EditorMarginSizePanel.vue";
    import EditorPredefinedImagesPanel from "./pageSettings/EditorPredefinedImagesPanel.vue";
    import EditorVariablesPanel from "./pageSettings/EditorVariablesPanel.vue";

    import { addAbsoluteTextBoxWithContent } from "./extensions/AbsoluteTextBox";

    const editorBackgroundColorModel = defineModel<string>("editorBackgroundColor", { required: true });
    const editorBackgroundImageModel = defineModel<string | null>("editorBackgroundImage", { required: true });
    const editorBackgroundImageIdModel = defineModel<string | null>("editorBackgroundImageId", { required: true });
    // const editorPaddingModel = defineModel<string>("editorPadding", { required: true });

    const props = defineProps<{
        editor: Editor;
        locationId: string;
        locationDetail: ILocationDetail | undefined;
    }>();

    const variablesPanel = ref<InstanceType<typeof EditorVariablesPanel>>();
    const imagesPanel = ref<InstanceType<typeof EditorPredefinedImagesPanel>>();

    const requiredVariables = computed(() => variablesPanel.value?.getRequiredVariables() ?? []);
    const requiredImages = computed(() => imagesPanel.value?.getRequiredImages() ?? []);

    function getRequiredVariablesAndImages() {
        return [...requiredVariables.value, ...requiredImages.value];
    }

    function addAbsoluteTextBox() {
        addAbsoluteTextBoxWithContent(props.editor, [
            {
                type: "paragraph",
                content: [
                    {
                        type: "text",
                        text: i18n("voucherDesign.variables"), // word "text" - empty text node is not allowed
                    },
                ],
            },
        ]);
    }

    defineExpose({
        getRequiredVariablesAndImages,
    });
</script>

<style scoped lang="scss">
    .editor-page-settings {
        width: 300px;
        background-color: #fdfcfe;
        border-top-right-radius: 0.5rem;
        border-bottom-right-radius: 0.5rem;
        border-left: 1px solid #ebedf2;
        padding: 1rem;

        & > h4 {
            font-size: 1.2rem;
            font-weight: 500;
            color: #48465b;
        }

        & > div:not(:first-child) {
            margin-top: 1rem;
        }
    }
</style>
